import { httpClient } from "@/shared/api";

import type { Service } from "../model/types";
import type {
  CreateServiceRequestDTO,
  ListServicesResponseDTO,
  RenameServiceRequestDTO,
  ServiceResponseDTO,
} from "./dto";

/** DTO (snake_case) → доменний `Service` (camelCase). Єдина точка мапінгу. */
function toDomain(dto: ServiceResponseDTO): Service {
  const service: Service = {
    id: dto.id,
    name: dto.name,
    assignedMasterCount: dto.assigned_master_count,
  };
  // Встановлюємо categoryId тільки коли поле є у DTO — щоб toEqual у старих тестах
  // (без category_id у fixture) не помічав зайвого поля.
  if (dto.category_id !== undefined) {
    service.categoryId = dto.category_id;
  }
  return service;
}

/**
 * `GET /api/services` — усі послуги компанії сесії (RLS scoping серверно).
 * 401 ловить глобальний interceptor (ADR-0006 D4); reject уже нормалізований
 * у `AppError` (ADR-0010 D4).
 */
export async function listServices(): Promise<Service[]> {
  const { data } = await httpClient.get<ListServicesResponseDTO>("/services");
  return data.services.map(toDomain);
}

/** `POST /api/services` (CSRF) — створює послугу; 201 → доменний `Service`. */
export async function createService(name: string, categoryId?: string | null): Promise<Service> {
  const body: CreateServiceRequestDTO = { name };
  if (categoryId !== undefined) {
    body.category_id = categoryId;
  }
  const { data } = await httpClient.post<ServiceResponseDTO>("/services", body);
  return toDomain(data);
}

/** `PATCH /api/services/{id}` (CSRF) — перейменування; 200 → доменний `Service`. */
export async function renameService(id: string, name: string, categoryId?: string | null): Promise<Service> {
  const body: RenameServiceRequestDTO = { name };
  if (categoryId !== undefined) {
    body.category_id = categoryId;
  }
  const { data } = await httpClient.patch<ServiceResponseDTO>(
    `/services/${id}`,
    body,
  );
  return toDomain(data);
}

/** `DELETE /api/services/{id}` (CSRF) — видалення; 204 без тіла. */
export async function deleteService(id: string): Promise<void> {
  await httpClient.delete(`/services/${id}`);
}
